import { useState } from 'react';

import { updateTeamApi } from '@/lib/api/teamsApi';
import { formatDateAbbreviatedFr } from '@shared';
import type { Team } from '@shared/types/entities';
import { useMutation } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { toast } from 'sonner';

interface EditTeamModalProps {
  team: Team;
  onClose: () => void;
  onSuccess: () => void;
}

export default function EditTeamModal({ team, onClose, onSuccess }: EditTeamModalProps) {
  const [name, setName] = useState(team.name);
  const [error, setError] = useState('');

  const mutation = useMutation({
    mutationFn: () => updateTeamApi(team.id, { name: name.trim() }),
    onSuccess: () => {
      toast.success('Equipe mise a jour');
      onSuccess();
    },
    onError: () => {
      toast.error('Erreur lors de la mise a jour');
    },
  });

  function handleSubmit() {
    if (!name.trim()) {
      setError('Le nom est obligatoire');
      return;
    }
    mutation.mutate();
  }

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
      <div className='absolute inset-0 bg-black/50 backdrop-blur-sm' onClick={onClose} />

      <div className='relative z-10 w-full max-w-sm bg-white rounded-2xl shadow-2xl p-6'>
        <div className='flex items-center justify-between mb-5'>
          <h2 className='text-base font-bold text-slate-900'>Modifier l&apos;equipe</h2>
          <button
            onClick={onClose}
            aria-label='Fermer'
            className='p-1.5 rounded-lg hover:bg-slate-100 transition-colors'
          >
            <X className='w-4 h-4 text-slate-400' />
          </button>
        </div>

        <div className='space-y-4'>
          <div>
            <label className='block text-sm font-semibold text-slate-700 mb-1.5'>
              Nom de l&apos;equipe <span className='text-red-500'>*</span>
            </label>
            <input
              type='text'
              value={name}
              onChange={e => {
                setName(e.target.value);
                setError('');
              }}
              maxLength={255}
              autoFocus
              className={`w-full px-3 py-2.5 border rounded-xl focus:outline-none text-sm ${
                error ? 'border-red-400' : 'border-slate-200 focus:border-emerald-500'
              }`}
              onKeyDown={e => e.key === 'Enter' && handleSubmit()}
            />
            {error && <p className='text-xs text-red-500 mt-1'>{error}</p>}
          </div>

          {team.competition && (
            <div className='bg-slate-50 rounded-xl p-3 text-sm text-slate-600'>
              <p className='font-semibold text-slate-800 truncate'>{team.competition.name}</p>
              <p className='text-xs text-slate-400 mt-0.5'>{formatDateAbbreviatedFr(team.competition.date)}</p>
            </div>
          )}
        </div>

        <div className='flex gap-3 mt-6'>
          <button
            onClick={onClose}
            disabled={mutation.isPending}
            className='flex-1 py-2.5 border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50'
          >
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={mutation.isPending}
            className='flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-xl text-sm font-semibold transition-colors'
          >
            {mutation.isPending ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}
